/**
 * PerplexityContentLightbox - WRAPPER COMPONENT (NO RESPONSIVE LOGIC)
 * 
 * This component is a clean wrapper that connects Perplexity content to TextContentLightbox.
 * 
 * ARCHITECTURE RULES:
 * - This component should NEVER contain responsive breakpoints (md:, lg:, sm:)
 * - All responsive behavior is controlled by TextContentLightbox (master)
 * - This component only handles Perplexity-specific configuration and rendering
 * 
 * COMPONENT CHAIN:
 * ResearchLightboxUniversal → PerplexityContentLightbox (this) → TextContentLightbox (master)
 * 
 * RESPONSIBILITIES:
 * - Define Perplexity-specific accessors and configuration
 * - Provide content and sidebar renderers
 * - Pass everything to TextContentLightbox for layout control
 * 
 * This is a GOOD EXAMPLE of clean architecture - pure wrapper with no layout logic!
 */
import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { TextContentLightbox } from '@/components/text-content-lightbox/TextContentLightbox';
import { TextContentAccessors, TextContentContext, TextContentSidebarContext } from '@/components/text-content-lightbox/types/TextContentLightbox';
import { PerplexityContent, PerplexityContentLightboxProps } from './types/PerplexityContent';
import { PerplexityContentRenderer } from './components/PerplexityContentRenderer';
import { PerplexitySidebarRenderer } from './components/PerplexitySidebarRenderer';

// Property accessors for Perplexity content
const perplexityAccessors: TextContentAccessors<PerplexityContent> = {
  getContentId: (content) => content.id,
  getContentTitle: (content) => content.title,
  getContentBody: (content) => content.content,
  getContentType: (content) => content.contentType,
  getContentMetadata: (content) => content.metadata || {}
};

export const PerplexityContentLightbox: React.FC<PerplexityContentLightboxProps> = ({
  content,
  isOpen,
  onClose,
  allContent = [],
  onNavigate,
  loadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoading = false,
  onRelatedQuestionClick,
  onSourceClick,
  onDelete,
  onDownload,
  checkOwnership,
  isGeneratingRelatedQuestion = false,
  onGenerationStateChange,
  showFavoriteButton = true, // Default to true for backward compatibility
  contentType = 'research', // NEW: Default to 'research' for backward compatibility
  showRelatedQuestions = true, // Default to true for backward compatibility
}) => {
  
  // Content renderer
  const renderContent = (item: PerplexityContent, context: TextContentContext<PerplexityContent>) => (
    <PerplexityContentRenderer
      content={item}
      context={context}
      onSourceClick={onSourceClick}
      showFavoriteButton={showFavoriteButton}
    />
  );

  // Sidebar renderer
  const renderSidebar = (item: PerplexityContent, context: TextContentSidebarContext<PerplexityContent>) => (
    <PerplexitySidebarRenderer
      content={item}
      context={context}
      onRelatedQuestionClick={onRelatedQuestionClick}
      onDelete={onDelete}
      onDownload={onDownload}
      checkOwnership={checkOwnership}
      isGeneratingRelatedQuestion={isGeneratingRelatedQuestion}
      onGenerationStateChange={onGenerationStateChange}
      contentType={contentType}
      showRelatedQuestions={showRelatedQuestions}
    />
  );

  // Loading component
  const loadingComponent = (
    <div className="space-y-6 p-6">
      <div className="border-b pb-4">
        <div className="flex items-center gap-3 mb-2">
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
        <Skeleton className="h-8 w-3/4" />
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-18" />
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );

  // Perplexity-specific configuration
  const config = {
    preloading: {
      enabled: true,
      strategy: 'smart' as const,
      preloadDistance: 2
    },
    navigation: {
      wheel: false,     // Disabled for text content
      keyboard: true,   // Arrow keys for navigation
      buttons: true     // Previous/Next buttons
    },
    mobile: {
      fullscreen: true,
      sidebarPosition: 'bottom' as const
    },
    features: {
      search: false,
      copy: true,
      download: true,
      print: false
    }
  };

  return (
    <TextContentLightbox
      item={content}
      isOpen={isOpen}
      onClose={onClose}
      allItems={allContent}
      onNavigate={onNavigate}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      accessors={perplexityAccessors}
      renderContent={renderContent}
      renderSidebar={renderSidebar}
      config={config}
      isLoading={isLoading}
      loadingComponent={loadingComponent}
      onDelete={onDelete}
      onDownload={onDownload}
      checkOwnership={checkOwnership}
    />
  );
};

export default PerplexityContentLightbox; 